import React from 'react';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

export interface NodeTooltipProps {
  /** Tooltip content */
  content: string;
  /** The element that triggers the tooltip */
  children: React.ReactNode;
  /** Position of the tooltip relative to the trigger */
  side?: TooltipSide;
  /** Maximum width of the tooltip */
  maxWidth?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
}

const positionClasses: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const arrowClasses: Record<TooltipSide, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800 border-x-transparent border-b-transparent border-t-4 border-x-4 border-b-0',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800 border-x-transparent border-t-transparent border-b-4 border-x-4 border-t-0',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800 border-y-transparent border-r-transparent border-l-4 border-y-4 border-r-0',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800 border-y-transparent border-l-transparent border-r-4 border-y-4 border-l-0',
};

/**
 * CSS-only tooltip component using Tailwind's group hover pattern
 * Displays a tooltip on hover without JavaScript state management
 */
const NodeTooltip: React.FC<NodeTooltipProps> = ({
  content,
  children,
  side = 'top',
  maxWidth = 200,
  className = '',
  disabled = false,
}) => {
  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <div className={`relative group inline-flex ${className}`}>
      {children}
      <div
        className={`absolute ${positionClasses[side]} px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 pointer-events-none`}
        style={{ maxWidth }}
      >
        {/* Arrow */}
        <div className={`absolute w-0 h-0 ${arrowClasses[side]}`} />
        {content}
      </div>
    </div>
  );
};

export default NodeTooltip;
